import { useT, useTx } from "@/lib/i18n-dict";
import { SHOW_PRICES } from "@/lib/pricing-visibility";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Database, Server, Users, Warehouse } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AddonCard } from "@/components/site/AddonCard";
import { PricingMatrix } from "@/components/site/PricingMatrix";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { ConsultCta, GhostCta, PrimaryCta } from "@/components/site/Cta";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { PRICING as PRICING_SOURCE } from "@/content/pages";
import { getPricing } from "@/lib/pricing.functions";
import { partitionPricingAddons } from "@/lib/pricing-categories";
import type { PricingAddon } from "@/lib/pricing.types";
import { faqScript } from "@/lib/faq-schema";
import { alternateLinks, localePath, normalizeLocale, SITE_URL, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const TITLE = "Pricing — plans for every stage of production";
const DESCRIPTION =
  "Compare factory.online plans, from Standard to Enterprise, and choose the right capacity for your production, team and item catalogue.";

/** Localised SEO copy: each language/region gets its own title, description and H1 suffix. */
const REGION_SEO: Record<
  Locale,
  {
    title: string;
    description: string;
    h1: string;
    h1Hidden?: string;
    priceLine: (price: string, region: string) => string;
  }
> = {
  en: {
    title: "Pricing in EUR — factory.online cloud MRP plans",
    description:
      "factory.online pricing in EUR: compare Standard, Pro and Enterprise cloud MRP plans, add-ons and per-user costs for your factory.",
    h1: "Pricing for manufacturers, in EUR",
    h1Hidden: "Pricing for manufacturers",
    priceLine: (price, region) => `Plans start at ${price} — ${region} pricing in EUR.`,
  },
  uk: {
    title: "Ціни в EUR для України — хмарна MRP factory.online",
    description:
      "Ціни factory.online для України в EUR: порівняйте тарифи хмарної MRP, доповнення та вартість на користувача для вашого виробництва.",
    h1: "Ціни для виробників в Україні (EUR)",
    h1Hidden: "Ціни для виробників в Україні",
    priceLine: (price, region) => `Тарифи від ${price} — ціни для регіону ${region} в EUR.`,
  },
  pl: {
    title: "Cennik w EUR dla Polski — chmurowy system MRP factory.online",
    description:
      "Cennik factory.online dla Polski w EUR: porównaj plany chmurowego MRP, dodatki i koszt na użytkownika dla Twojej produkcji.",
    h1: "Cennik dla producentów w Polsce (EUR)",
    h1Hidden: "Cennik dla producentów w Polsce",
    priceLine: (price, region) => `Plany od ${price} — ceny dla regionu ${region} w EUR.`,
  },
  ar: {
    title: "الأسعار باليورو للشرق الأوسط — منصة factory.online السحابية",
    description:
      "أسعار factory.online للشرق الأوسط باليورو: قارن خطط نظام MRP السحابي والإضافات وتكلفة المستخدم لمصنعك.",
    h1: "أسعار المصنّعين في الشرق الأوسط (EUR)",
    h1Hidden: "أسعار المصنّعين في الشرق الأوسط",
    priceLine: (price, region) => `تبدأ الخطط من ${price} — أسعار منطقة ${region} باليورو.`,
  },
  hi: {
    title: "भारत के लिए EUR में मूल्य — factory.online क्लाउड MRP",
    description:
      "भारत के लिए factory.online के EUR मूल्य: क्लाउड MRP प्लान, ऐड-ऑन और प्रति उपयोगकर्ता लागत की तुलना करें।",
    h1: "भारत में विनिर्माताओं के लिए मूल्य (EUR)",
    h1Hidden: "भारत में विनिर्माताओं के लिए मूल्य",
    priceLine: (price, region) => `प्लान ${price} से शुरू — ${region} क्षेत्र के EUR मूल्य।`,
  },
  uz: {
    title: "O'zbekiston uchun EUR narxlari — factory.online bulutli MRP",
    description:
      "factory.online narxlari O'zbekiston uchun EUR'da: bulutli MRP tariflari, qo'shimchalar va foydalanuvchi narxini solishtiring.",
    h1: "O'zbekistondagi ishlab chiqaruvchilar uchun narxlar (EUR)",
    h1Hidden: "O'zbekistondagi ishlab chiqaruvchilar uchun narxlar",
    priceLine: (price, region) => `Tariflar ${price} dan — ${region} mintaqasi uchun EUR narxlari.`,
  },
  de: {
    title: "Preise in EUR für Deutschland — Cloud-MRP factory.online",
    description:
      "factory.online Preise in EUR: Cloud-MRP-Tarife, Add-ons und Kosten pro Nutzer für Fertigungsbetriebe in Deutschland vergleichen.",
    h1: "Preise für Fertigungsbetriebe in Deutschland (EUR)",
    h1Hidden: "Preise für Fertigungsbetriebe in Deutschland",
    priceLine: (price, region) => `Tarife ab ${price} — Preise für die Region ${region} in EUR.`,
  },
  fr: {
    title: "Tarifs en EUR pour la France — MRP cloud factory.online",
    description:
      "Tarifs factory.online en EUR : comparez les offres MRP cloud, les modules complémentaires et le coût par utilisateur pour votre usine.",
    h1: "Tarifs pour les industriels en France (EUR)",
    h1Hidden: "Tarifs pour les industriels en France",
    priceLine: (price, region) =>
      `Offres à partir de ${price} — tarifs de la région ${region} en EUR.`,
  },
  it: {
    title: "Prezzi in EUR per l'Italia — MRP in cloud factory.online",
    description:
      "Prezzi factory.online in EUR: confronta i piani MRP in cloud, i moduli aggiuntivi e il costo per utente per la tua azienda manifatturiera.",
    h1: "Prezzi per le aziende manifatturiere in Italia (EUR)",
    h1Hidden: "Prezzi per le aziende manifatturiere in Italia",
    priceLine: (price, region) => `Piani da ${price} — prezzi per la regione ${region} in EUR.`,
  },
  th: {
    title: "ราคา EUR สำหรับประเทศไทย — ระบบ MRP บนคลาวด์ factory.online",
    description:
      "ราคา factory.online ใน EUR สำหรับประเทศไทย: เปรียบเทียบแผน MRP บนคลาวด์ ส่วนเสริม และต้นทุนต่อผู้ใช้สำหรับโรงงานของคุณ",
    h1: "ราคาสำหรับผู้ผลิตในประเทศไทย (EUR)",
    h1Hidden: "ราคาสำหรับผู้ผลิตในประเทศไทย",
    priceLine: (price, region) => `แพ็กเกจเริ่มต้นที่ ${price} — ราคา EUR สำหรับภูมิภาค ${region}`,
  },
  ur: {
    title: "پاکستان کے لیے EUR میں قیمتیں — factory.online کلاؤڈ MRP",
    description:
      "پاکستان کے لیے EUR میں factory.online کی قیمتیں: اپنے کارخانے کے لیے کلاؤڈ MRP پلان، ایڈ آنز اور فی صارف لاگت کا موازنہ کریں۔",
    h1: "پاکستان میں مینوفیکچررز کے لیے قیمتیں (EUR)",
    h1Hidden: "پاکستان میں مینوفیکچررز کے لیے قیمتیں",
    priceLine: (price, region) =>
      `پلان ${price} سے شروع ہوتے ہیں — ${region} خطے کے لیے EUR قیمتیں۔`,
  },
};

function lowestPlanPrice(plans: { price: string }[]): string {
  return plans.find((plan) => /\d/.test(plan.price))?.price ?? "";
}

export const Route = createFileRoute("/{-$locale}/prices")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    return { pricing: await getPricing({ data: { locale } }), locale };
  },
  head: ({ loaderData }) => {
    const locale = loaderData?.locale ?? "en";
    const seo = REGION_SEO[locale] ?? REGION_SEO.en;
    const plans = loaderData?.pricing.plans ?? [];
    const regionName = loaderData?.pricing.region?.name ?? "";
    const from = lowestPlanPrice(plans);
    const title = loaderData ? seo.title : TITLE;
    const description = loaderData
      ? from && SHOW_PRICES
        ? `${seo.description} ${seo.priceLine(from, regionName || seo.h1)}`.trim()
        : seo.description
      : DESCRIPTION;
    const url = `${SITE_URL}${localePath(locale, "/prices")}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:locale", content: locale },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: `${SITE_URL}/og/og-prices.jpg` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:image", content: `${SITE_URL}/og/og-prices.jpg` },
      ],
      links: [{ rel: "canonical", href: url }, ...alternateLinks("/prices")],
      scripts: [
        ...(plans.length && SHOW_PRICES
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Product",
                  name: "factory.online cloud MRP",
                  description,
                  brand: { "@type": "Brand", name: "factory.online" },
                  offers: plans
                    .filter((plan) => /\d/.test(plan.price))
                    .map((plan) => ({
                      "@type": "Offer",
                      name: plan.name,
                      price: plan.price
                        .replace(/[^\d.,]/g, "")
                        .replace(/\s/g, "")
                        .replace(/,(?=\d{3}\b)/g, ""),
                      priceCurrency: "EUR",
                      url,
                      availability: "https://schema.org/InStock",
                      eligibleRegion: regionName || undefined,
                    })),
                }),
              },
            ]
          : []),
        ...faqScript(PRICING_SOURCE.faq, locale),
      ],
    };
  },
  component: Prices,
  errorComponent: () => (
    <Section>
      <p className="text-center text-muted-foreground">
        Pricing is temporarily unavailable. Please try again shortly.
      </p>
    </Section>
  ),
});

function Prices() {
  const PRICING = useTx(PRICING_SOURCE);
  const t = useT();
  const { pricing, locale } = Route.useLoaderData();
  // Pricing records are managed in English in the CMS. Their visible fields use
  // the same source-string dictionary as static pricing copy.
  const localizedPricing = useTx(pricing);
  const { plans, groups, addons } = localizedPricing;
  const { meteredFeatures, moduleAddons } = partitionPricingAddons(addons);
  const seo = REGION_SEO[locale] ?? REGION_SEO.en;
  const fromPrice = lowestPlanPrice(plans);
  const planById = new Map(plans.map((p) => [p.id, p]));

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 text-center md:py-28">
          <Reveal className="mx-auto max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {PRICING.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {SHOW_PRICES ? seo.h1 : (seo.h1Hidden ?? seo.h1)}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">{PRICING.intro.lead}</p>
            {SHOW_PRICES && fromPrice ? (
              <p className="mt-3 text-base font-medium text-foreground/90">
                {seo.priceLine(fromPrice, localizedPricing.region?.name ?? "")}
              </p>
            ) : null}
          </Reveal>
        </div>
      </section>

      {/* Plans */}
      <Section>
        {SHOW_PRICES ? (
          <p className="text-center text-sm text-muted-foreground">{PRICING.billingNote}</p>
        ) : null}
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan, index) => (
            <Reveal
              key={plan.id}
              delay={index * 40}
              className={cn(
                "flex h-full flex-col rounded-xl border p-6",
                plan.highlighted
                  ? "border-primary/40 bg-surface shadow-[var(--glow-primary)] lg:-translate-y-2"
                  : "border-border bg-surface/40",
              )}
            >
              {plan.highlighted ? (
                <span className="mb-3 inline-flex w-fit rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  {t("Most popular")}
                </span>
              ) : null}
              <h3 className="font-display text-lg font-bold">{plan.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{plan.blurb}</p>
              <p className="mt-5">
                <span className="font-display text-3xl font-bold">
                  {SHOW_PRICES ? plan.price : t("On request")}
                </span>
                {SHOW_PRICES && plan.period ? (
                  <span className="text-sm text-muted-foreground"> {plan.period}</span>
                ) : null}
              </p>
              <ul className="mt-6 flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-3 text-base text-muted-foreground">
                    <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {plan.highlighted ? (
                  <PrimaryCta to="/registration-demo" className="w-full">
                    {plan.cta}
                  </PrimaryCta>
                ) : (
                  <GhostCta to="/registration-demo" className="w-full">
                    {plan.cta}
                  </GhostCta>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Interactive comparison matrix */}
      {groups.length && plans.length ? (
        <Section id="comparison" className="scroll-mt-24 bg-surface/40">
          <SectionHeading
            eyebrow="Compare"
            title="Plans and add-ons, side by side"
            lead="Filter by plan or add-on, search a capability, or show only what differs."
          />
          <PricingMatrix plans={plans} groups={groups} addons={moduleAddons} />
        </Section>
      ) : null}

      {/* Metered features */}
      {meteredFeatures.length ? (
        <Section className="bg-surface/40">
          <SectionHeading
            eyebrow="Flexible capacity"
            title="Metered features"
            lead="Add capacity as your operation grows without changing the rest of your plan."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {meteredFeatures.map((feature, index) => (
              <MeteredFeatureCard
                key={feature.id}
                feature={feature}
                planNames={feature.planIds
                  .map((id) => planById.get(id)?.name)
                  .filter((name): name is string => Boolean(name))}
                index={index}
              />
            ))}
          </div>
        </Section>
      ) : null}

      {/* Add-ons */}
      {moduleAddons.length ? (
        <Section>
          <SectionHeading
            eyebrow="Add-ons"
            title="Extend any plan with add-ons"
            lead="Independent modules you can attach to your subscription when your operation needs them."
          />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {moduleAddons.map((addon, index) => (
              <AddonCard
                key={addon.id}
                addon={addon}
                delay={index * 40}
                planNames={addon.planIds
                  .map((id) => planById.get(id)?.name)
                  .filter((n): n is string => Boolean(n))}
              />
            ))}
          </div>
        </Section>
      ) : null}

      {/* Comparison notes */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="Good to know" title="How the plans compare" />
        <ul className="mt-8 grid gap-4 md:grid-cols-3">
          {PRICING.comparisonNotes.map((note, index) => (
            <Reveal
              key={note}
              delay={index * 60}
              className="rounded-xl border border-border p-6 text-sm text-muted-foreground"
            >
              {note}
            </Reveal>
          ))}
        </ul>
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <SectionHeading eyebrow="FAQ" title="Pricing questions, answered" />
        <Reveal className="mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {PRICING.faq.map((faq, index) => (
              <AccordionItem key={faq.q} value={`pricing-faq-${index}`}>
                <AccordionTrigger className="text-start font-display text-base">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </Section>

      <RelatedLinks variant="prices" />
      <ConsultCta />
    </>
  );
}

const METERED_ICONS = {
  "extra-user": Users,
  "extra-storage-location": Warehouse,
  "extra-storage-space": Database,
  "extra-environment": Server,
};

function MeteredFeatureCard({
  feature,
  planNames,
  index,
}: {
  feature: PricingAddon;
  planNames: string[];
  index: number;
}) {
  const t = useT();
  const Icon = METERED_ICONS[feature.slug as keyof typeof METERED_ICONS] ?? Database;

  return (
    <Reveal delay={index * 40} className="flex h-full flex-col border-t border-primary/50 pt-6">
      <Icon className="size-6 text-primary" aria-hidden />
      <h3 className="mt-4 font-display text-xl font-bold">{t(feature.name)}</h3>
      <p className="mt-2 flex-1 text-base text-muted-foreground">{t(feature.description)}</p>
      <p className="mt-6 font-display text-2xl font-bold">
        {SHOW_PRICES ? feature.price : t("On request")}
        {SHOW_PRICES && feature.period ? (
          <span className="ml-1 text-sm font-normal text-muted-foreground">{feature.period}</span>
        ) : null}
      </p>
      {planNames.length ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("Available for")}</span>
          {planNames.map((name) => (
            <span
              key={name}
              className="rounded-full border border-border px-2.5 py-0.5 text-xs leading-relaxed text-muted-foreground"
            >
              {name}
            </span>
          ))}
        </div>
      ) : null}
    </Reveal>
  );
}
